// src/pages/Events/EventSelection.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Search, Loader, Plus, Minus, ListChecks } from "lucide-react";
import api from "@/lib/axios";
import { createPlanToken, replenishmentUrl } from "@/lib/eventTokens";

const asNum = (v) => (v === "" || v == null ? null : Number(v));

export default function EventSelection() {
  const { id } = useParams(); // optional event id
  const navigate = useNavigate();
  const qs = new URLSearchParams(useLocation().search);
  const action = qs.get("action"); // if "stockcheck", auto open stock flow after load

  // menu search
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  // cart: { [menu_item_id]: { servings: number, event_sale_price?: number } }
  const [cart, setCart] = useState({});

  // event meta (we only pass thru; not persisted here)
  const [meta, setMeta] = useState({
    event_id: id || null,
    event_name: "",
    planner: "",
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const { data } = await api.get("/inventory/menu-items", { params: q ? { q } : {} });
        const rows = Array.isArray(data)
          ? data
          : Array.isArray(data?.rows)
          ? data.rows
          : Array.isArray(data?.items)
          ? data.items
          : Array.isArray(data?.data)
          ? data.data
          : [];
        if (!cancelled) setList(rows);
      } catch (e) {
        if (!cancelled) setList([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [q]);

  const add = (m) => {
    setCart((c) => {
      const prev = c[m.menu_item_id] || { servings: 0, event_sale_price: null };
      return { ...c, [m.menu_item_id]: { ...prev, servings: (prev.servings || 0) + 1 } };
    });
  };

  const sub = (m) => {
    setCart((c) => {
      const prev = c[m.menu_item_id] || { servings: 0, event_sale_price: null };
      const nextServ = Math.max(0, (prev.servings || 0) - 1);
      return { ...c, [m.menu_item_id]: { ...prev, servings: nextServ } };
    });
  };

  const entries = useMemo(
    () => Object.entries(cart).filter(([, v]) => (v?.servings ?? 0) > 0),
    [cart]
  );

  const onCheckStock = async () => {
    if (entries.length === 0) {
      alert("Add at least one dish/drink with servings.");
      return;
    }
    const items = entries.map(([menu_item_id, v]) => ({
      menu_item_id: Number(menu_item_id),
      servings_requested: Number(v.servings || 0),
      event_sale_price:
        v.event_sale_price == null || v.event_sale_price === ""
          ? null
          : Number(v.event_sale_price),
    }));

    const { token } = await createPlanToken({
      location_id: 10, // MAIN
      items,
      meta,
    });

    // Redirect to replenishment view (token mode)
    window.location.assign(replenishmentUrl(token));
  };

  return (
    <div className="min-h-screen p-6 bg-gradient-to-br from-black via-gray-900 to-gray-950 text-white">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="text-2xl font-extrabold">🍽️ Event Menu Selection</h1>
          <p className="text-gray-400 text-sm">
            Pick dishes & drinks, set servings, then run a stock check against Main Store.
          </p>
        </div>
        <button
          onClick={onCheckStock}
          className="bg-emerald-600 hover:bg-emerald-700 px-3 py-2 rounded inline-flex items-center gap-2"
        >
          <ListChecks className="w-4 h-4" />
          Check stock & shortages
        </button>
      </div>

      {/* Event meta (pass-through only) */}
      <div className="mb-5 grid md:grid-cols-3 gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-xs text-gray-400">Event Name</span>
          <input
            className="bg-[#161616] border border-gray-700 rounded-lg px-3 py-2 outline-none"
            value={meta.event_name}
            onChange={(e) => setMeta((p) => ({ ...p, event_name: e.target.value }))}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-gray-400">Planner</span>
          <input
            className="bg-[#161616] border border-gray-700 rounded-lg px-3 py-2 outline-none"
            value={meta.planner}
            onChange={(e) => setMeta((p) => ({ ...p, planner: e.target.value }))}
          />
        </label>
        <div className="flex items-end">
          <button
            onClick={() => navigate("/events")}
            className="w-full px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg"
          >
            Back to dashboard
          </button>
        </div>
      </div>

      {/* Search + List */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            placeholder="Search menu items…"
            className="w-full pl-9 pr-3 py-2 rounded bg-gray-900/60 border border-white/10"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-full text-center text-gray-400">
            <Loader className="w-4 h-4 inline-block animate-spin mr-2" /> Loading…
          </div>
        ) : (Array.isArray(list) ? list : []).map((m) => {
            const rowId = m.menu_item_id ?? m.id;
            const buf = cart[rowId] || { servings: 0, event_sale_price: "" };
            const existingPrice = m.manual_sale_price_override ?? null;
            return (
              <div key={rowId} className="bg-white/5 border border-white/10 rounded-xl p-4">
                <div className="font-semibold">{m.name ?? m.menu_item_name ?? "—"}</div>
                <div className="text-xs text-gray-400">{m.description || "—"}</div>
                <div className="text-xs mt-2 text-gray-300">
                  Auto cost: £{Number(m.auto_cost || 0).toFixed(2)}{" "}
                  {m.manual_cost_override != null && (
                    <span className="ml-1 text-amber-300">(override)</span>
                  )}
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <button
                    onClick={() => sub({ menu_item_id: rowId })}
                    className="p-2 bg-gray-800 hover:bg-gray-700 rounded"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={buf.servings}
                    onChange={(e) =>
                      setCart((c) => ({
                        ...c,
                        [rowId]: {
                          ...buf,
                          servings: Math.max(0, Number(e.target.value || 0)),
                        },
                      }))
                    }
                    className="w-24 px-2 py-1 rounded bg-black/50 border border-white/10 text-center"
                  />
                  <button
                    onClick={() => add({ menu_item_id: rowId })}
                    className="p-2 bg-gray-800 hover:bg-gray-700 rounded"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <div className="mt-3">
                  <label className="text-xs text-gray-400">
                    Per-event selling price (£){" "}
                    <span className="text-gray-500">
                      (fallback {existingPrice != null ? `£${Number(existingPrice).toFixed(2)}` : "—"})
                    </span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={buf.event_sale_price ?? ""}
                    onChange={(e) =>
                      setCart((c) => ({
                        ...c,
                        [rowId]: { ...buf, event_sale_price: e.target.value },
                      }))
                    }
                    placeholder={existingPrice != null ? String(existingPrice) : ""}
                    className="mt-1 w-full bg-[#161616] border border-gray-700 rounded-lg px-3 py-2 outline-none"
                  />
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
}
